import type { MarketingIndustrySlug } from './industry-page-model';
import type { MarketingIndustryPageSource } from './industry-page-content.types';

type FinalIndustrySlug = Extract<
  MarketingIndustrySlug,
  'toys' | 'sporting-goods'
>;

export const MARKETING_INDUSTRY_PAGE_CONTENT_C = {
  toys: {
    heroBody:
      'Turn one play idea into a complete toy line—hero product, variants, accessories, playsets, development records, packaging, and launch.',
    categories: [
      'Figures & Dolls',
      'Plush',
      'Vehicles',
      'Playsets',
      'Preschool',
      'Collectibles',
    ],
    benefitsHeading: 'From one brief to the complete line',
    benefits: [
      {
        title: 'More concepts, faster',
        body: 'Explore more play and product directions before selecting what to develop.',
      },
      {
        title: 'Build the whole range together',
        body: 'Create the hero toy, variants, accessories, and playsets as one product world.',
      },
      {
        title: 'Fewer late changes',
        body: 'Carry approved decisions into development before prototypes and tooling.',
      },
    ],
    workflowHeading: 'One toy, connected from play brief to shelf',
    workflowSteps: [
      {
        number: '01',
        title: 'Define the Play',
        body: 'Set the audience, age range, play pattern, target price, sales channel, brand rules, and initial safety requirements.',
      },
      {
        number: '02',
        title: 'Create the Hero Toy',
        body: 'Explore characters, forms, proportions, expressions, poses, colors, accessories, and play features.',
      },
      {
        number: '03',
        title: 'Build the Product World',
        body: 'Develop companions, outfits, accessories, vehicles, playsets, exclusives, and collector variants.',
      },
      {
        number: '04',
        title: 'Prepare and Launch',
        body: 'Organize product views, components, materials, BOMs, assembly, test-plan inputs, packaging, and retailer content.',
      },
    ],
    capabilitiesHeading: 'Design the whole play system',
    capabilities: [
      {
        title: 'Play and audience',
        body: 'Intended age, play pattern, interaction, skill and supervision inputs, and repeat-play opportunities.',
      },
      {
        title: 'Product and range',
        body: 'Characters, silhouettes, scale, expressions, poses, accessories, features, and family variants.',
      },
      {
        title: 'Development and safety',
        body: 'Components, materials, dimensions, assembly, hazards, tests, warnings, and instructions.',
      },
      {
        title: 'Shelf and story',
        body: 'Packaging, assortments, retailer presentations, demonstrations, localization, and campaign video.',
      },
    ],
    systemsHeading: 'Keep your toy development systems',
    systemsBody:
      'Work alongside CAD, PLM, ERP, supplier, quality, compliance, licensing, PIM, DAM, and commerce systems while qualified teams retain engineering, play-testing, and safety responsibility.',
    recordHeading: 'Or connect the complete product world in My Drive',
    recordBody:
      'Keep play briefs, characters, licence references, products, components, materials, BOMs, packaging, suppliers, tests, warnings, tracking, retail data, imagery, instructions, and video together.',
    faq: {
      label: 'Frequently Asked Questions',
      heading: 'FAQ',
      items: [
        {
          question: 'Does IMAI replace play testing?',
          answer:
            'No. IMAI can define play scenarios and organize feedback, but observing children and parents remains essential.',
        },
        {
          question: 'Does IMAI replace toy CAD or engineering?',
          answer:
            'No. Specialist systems and engineers remain responsible for mechanisms, geometry, tolerances, tooling, and manufacturing validation.',
        },
        {
          question: 'Does IMAI certify toy safety?',
          answer:
            'No. IMAI keeps requirements, test plans, reports, warnings, and approvals connected. Qualified teams and laboratories remain responsible.',
        },
        {
          question: 'Can IMAI support licensed characters?',
          answer:
            'Yes. Approved references, proportions, colors, brand rules, feedback, and approvals can remain connected to every product and variant.',
        },
      ],
    },
    cta: {
      eyebrow: 'One hero toy. A complete play world.',
      heading: 'Start with one toy line.',
      cards: [
        { label: 'Build a Toy Line', href: '/auth?mode=signup' },
        { label: 'Discuss a Toy Line Pilot', href: '/contact' },
      ],
    },
  },
  'sporting-goods': {
    heroBody:
      'Explore more performance directions, develop every variant, and prepare a clear engineering handoff before the next physical prototype.',
    categories: [
      'Team Sports',
      'Racquet & Golf',
      'Fitness',
      'Outdoor',
      'Cycling',
      'Protective Gear',
    ],
    benefitsHeading: 'Turn an athlete problem into a product',
    benefits: [
      {
        title: 'Explore more directions',
        body: 'Compare concepts, materials, and constructions around a defined athlete need.',
      },
      {
        title: 'Reduce prototype cycles',
        body: 'Resolve design, variant, and specification decisions before physical development.',
      },
      {
        title: 'Sell proven performance',
        body: 'Turn approved specifications and test evidence into compelling product content.',
      },
    ],
    workflowHeading: 'From athlete insight to market',
    workflowSteps: [
      {
        number: '01',
        title: 'Define the Athlete Problem',
        body: 'Capture the athlete, environment, skill level, performance need, target price, and applicable standards.',
        output: 'Performance brief',
      },
      {
        number: '02',
        title: 'Explore Product Directions',
        body: 'Develop form, ergonomics, materials, construction, components, sizes, and variants.',
        output: 'Approved concept',
      },
      {
        number: '03',
        title: 'Prepare for Development',
        body: 'Organize views, dimensions, components, BOM inputs, cost, tolerances, assembly, and test requirements.',
        output: 'Test-ready handoff',
      },
      {
        number: '04',
        title: 'Test and Refine',
        body: 'Connect lab results, athlete trials, field feedback, and failures to every prototype revision.',
        output: 'Approved product and evidence',
      },
      {
        number: '05',
        title: 'Launch the Product Line',
        body: 'Create imagery, comparisons, retailer assets, product pages, campaigns, and videos.',
        output: 'Channel-ready content',
      },
    ],
    capabilitiesHeading: 'Sporting-goods capabilities',
    capabilities: [
      {
        title: 'Athlete-led concepts',
        body: 'Build product directions around real users, environments, and performance needs.',
      },
      {
        title: 'Product-line variants',
        body: 'Develop size, weight, skill-level, material, color, and market versions.',
      },
      {
        title: 'Development and testing',
        body: 'Connect specifications, prototypes, test results, feedback, revisions, and approvals.',
      },
      {
        title: 'Performance marketing',
        body: 'Create product education, feature visuals, comparison charts, and launch content from approved evidence.',
      },
    ],
    systemsHeading: 'Keep your performance-product systems',
    systemsBody:
      'Continue using CAD, CAE, PLM, QMS, ERP/MRP, laboratory, PIM, DAM, and commerce systems. IMAI connects creation and product decisions without replacing engineering or certification.',
    recordHeading: 'Or make My Drive the complete system of record',
    recordBody:
      'Keep athlete research, performance targets, concepts, variants, materials, prototypes, test results, claims, suppliers, quality records, content, feedback, warranty, and repair connected.',
    faq: {
      label: 'Frequently Asked Questions',
      heading: 'FAQ',
      items: [
        {
          question: 'What categories can IMAI support?',
          answer:
            'Team sports, racquet and golf, fitness, outdoor, cycling, protective gear, and connected accessories.',
        },
        {
          question: 'Does IMAI replace CAD or simulation?',
          answer:
            'No. IMAI supports product definition, variants, and handoff. Detailed engineering remains in specialist systems.',
        },
        {
          question: 'Can IMAI validate performance or safety?',
          answer:
            'No. It organizes requirements and evidence. Qualified teams perform testing and certification.',
        },
        {
          question: 'Can athlete feedback be connected to revisions?',
          answer:
            'Yes. Feedback can be attached to the relevant prototype, test round, and product version.',
        },
      ],
    },
    cta: {
      eyebrow: 'Every test should improve the product',
      heading: 'Turn one athlete problem into your next product line.',
      cards: [
        { label: 'Build a Sporting Goods Line', href: '/auth?mode=signup' },
        { label: 'Book a Sporting Goods Demo', href: '/contact' },
      ],
    },
  },
} as const satisfies Record<FinalIndustrySlug, MarketingIndustryPageSource>;
